import { useEffect, useRef, useState } from "react";

// Phase 2a rental-agreement FR-3 — canvas-based signature pad. Mouse +
// touch + pointer events. Exposes the captured PNG as a data URL via
// `onChange` and provides a "Type instead" alternative for
// accessibility (a11y FR-7).

interface SignaturePadProps {
  onChange: (value: { dataUrl: string; type: "drawn" | "typed" }) => void;
  initialName?: string;
}

export function SignaturePad({ onChange, initialName = "" }: SignaturePadProps): JSX.Element {
  const [mode, setMode] = useState<"drawn" | "typed">("drawn");
  const [typed, setTyped] = useState(initialName);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
  }, []);

  function pointAt(e: React.PointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) * canvas.width) / rect.width,
      y: ((e.clientY - rect.top) * canvas.height) / rect.height
    };
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current!;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const ctx = canvas.getContext("2d")!;
    const { x, y } = pointAt(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current!.getContext("2d")!;
    const { x, y } = pointAt(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  }

  function handlePointerUp() {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const dataUrl = canvasRef.current!.toDataURL("image/png");
    onChange({ dataUrl, type: "drawn" });
  }

  function handleClear() {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    onChange({ dataUrl: "", type: "drawn" });
  }

  function buildTypedDataUrl(value: string): string {
    const canvas = document.createElement("canvas");
    canvas.width = 600;
    canvas.height = 200;
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#111";
    ctx.font = "italic 40px 'Brush Script MT', cursive";
    ctx.textBaseline = "middle";
    ctx.fillText(value || " ", 20, canvas.height / 2);
    return canvas.toDataURL("image/png");
  }

  function handleTypedChange(value: string) {
    setTyped(value);
    onChange({ dataUrl: value ? buildTypedDataUrl(value) : "", type: "typed" });
  }

  return (
    <div className="space-y-2" data-testid="signature-pad">
      <div role="tablist" className="flex gap-2 text-sm">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "drawn"}
          className={`rounded border px-3 py-1 ${mode === "drawn" ? "border-brand-red bg-brand-red/10" : "border-zinc-300"}`}
          onClick={() => setMode("drawn")}
        >
          Draw
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "typed"}
          className={`rounded border px-3 py-1 ${mode === "typed" ? "border-brand-red bg-brand-red/10" : "border-zinc-300"}`}
          onClick={() => setMode("typed")}
        >
          Type instead
        </button>
      </div>

      {mode === "drawn" ? (
        <div>
          <canvas
            ref={canvasRef}
            width={600}
            height={200}
            data-testid="signature-canvas"
            aria-label="Signature pad — draw with mouse or finger"
            className="block w-full max-w-xl rounded border border-zinc-300 bg-white touch-none"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          />
          <button
            type="button"
            className="mt-2 text-sm text-brand-red underline"
            onClick={handleClear}
          >
            Clear
          </button>
        </div>
      ) : (
        <div>
          <label htmlFor="typed-signature" className="block text-sm font-medium">
            Type your signature
          </label>
          <input
            id="typed-signature"
            type="text"
            value={typed}
            onChange={(e) => handleTypedChange(e.target.value)}
            className="mt-1 w-full max-w-xl rounded border border-zinc-300 px-3 py-2 italic"
            style={{ fontFamily: "'Brush Script MT', cursive", fontSize: "1.5rem" }}
          />
        </div>
      )}
    </div>
  );
}
